import type { ReactNode } from 'react';
import { fmtMoney, fmtCompact } from '../utils/format';

interface Props {
  value: number;
  /** Use K/M compact format. */
  compact?: boolean;
  /** Color the text based on sign or polarity: auto follows `value`, 'pos'/'neg'/'muted' force. */
  tone?: 'auto' | 'pos' | 'neg' | 'muted' | 'default';
  /** Explicitly prepend a sign even for positive values. */
  sign?: boolean;
  /** Override the default mono font weight. */
  bold?: boolean;
  /** Size: defaults to inherited; 'lg' and 'xl' match dashboard scale. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  decimals?: number;
  prefix?: ReactNode;
}

const SIZE = {
  xs: 'text-[11px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

export function Money({
  value,
  compact,
  tone = 'default',
  sign,
  bold,
  size,
  decimals,
  prefix,
}: Props) {
  const isNeg = value < 0;
  const colorCls =
    tone === 'auto'
      ? isNeg
        ? 'text-neg-700 dark:text-neg-500'
        : value > 0
          ? 'text-pos-700 dark:text-pos-500'
          : 'text-ink-500 dark:text-ink-300'
      : tone === 'pos'
        ? 'text-pos-700 dark:text-pos-500'
        : tone === 'neg'
          ? 'text-neg-700 dark:text-neg-500'
          : tone === 'muted'
            ? 'text-ink-500 dark:text-ink-300'
            : '';

  const text = compact ? fmtCompact(value) : fmtMoney(Math.abs(value), { decimals });
  const prefixChar = sign && value > 0 ? '+' : isNeg && compact ? '−' : '';
  const body = compact ? text : value < 0 ? `−${text}` : text;

  return (
    <span
      className={`font-mono tabular-nums ${bold ? 'font-bold' : 'font-semibold'} ${size ? SIZE[size] : ''} ${colorCls}`}
    >
      {prefix}
      {prefixChar && !body.startsWith('−') ? prefixChar : ''}
      {compact ? `$${body}` : body}
    </span>
  );
}
